import Link from 'next/link';
import { ChevronRight, Folder, Share2 } from 'lucide-react';
import { displayName, extensionOf, humanSize, inlineByteSize } from '../../../lib/artifact-file';
import { creatorOf, folderCountLabel, folderWorkspaceName, pathSegments, type FileFolderRow } from '../../../lib/file-browser';
import { useTranslation } from '../../../lib/i18n';
import { FileCreatedBy } from '../FileCreatedBy';
import { FileGraphic } from '../FileArtifactCard';
import { Card, IconButton, OverflowMenu, type OverflowMenuAction } from '../ui';
import styles from './FilesGrid.module.css';
import type { FilesRow } from './files-row';

// The same files as tiles. The thumbnail is `FileGraphic` — the SAME graphic the workspace screen's file cards
// use (an image file previews itself, everything else gets its kind glyph on a neutral well), so a file looks
// the same wherever you meet it rather than being drawn twice.
//
// Everything except the layout is shared with the list: the same rows, the same actions, the same share button,
// the same click behaviour (a real link underneath, plain click opens the viewer).
export function FilesGrid({
  rows,
  folders,
  onOpenFolder,
  showFolderWorkspace = false,
  workspaceNames = {},
  onOpen,
  onShare,
  actionsFor,
  onRowMenu,
  selectedName,
}: {
  rows: FilesRow[];
  /** The current folder's subfolders — tiles in the SAME grid, because a folder and a file are both things
   *  sitting in this directory, and a row of folders above a grid of tiles reads as two separate lists. */
  folders: FileFolderRow[];
  onOpenFolder: (path: string) => void;
  /** Name each folder's owning Space — the merged root only. */
  showFolderWorkspace?: boolean;
  workspaceNames?: Record<string, string>;
  onOpen: (row: FilesRow) => void;
  onShare: (row: FilesRow) => void;
  actionsFor: (row: FilesRow) => OverflowMenuAction[][];
  onRowMenu?: (row: FilesRow) => void;
  selectedName?: string;
}) {
  const { t } = useTranslation();
  return (
    <div className={styles.grid}>
      {folders.map((folder) => (
        <FolderTile
          key={folder.path}
          folder={folder}
          workspaceNames={workspaceNames}
          showWorkspace={showFolderWorkspace}
          onOpen={onOpenFolder}
        />
      ))}
      {rows.map((row) => {
        const size = inlineByteSize(row.item);
        const creator = creatorOf(row.item);
        return (
          <Card
            key={row.key}
            variant="outline"
            size="sm"
            interactive
            selected={selectedName != null && row.item.name === selectedName}
            className={styles.tile}
            disablePadding
          >
            <Link
              href={row.href}
              as={row.as}
              className={styles.tileLink}
              onClick={(e) => {
                if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
                e.preventDefault();
                onOpen(row);
              }}
            >
              <FileGraphic item={row.item} square basePath={row.basePath} thumbHeight={104} />
              <span className={styles.body}>
                <span className={styles.name}>{displayName(row.item)}</span>
                {/* A tile has a line to spare and no column to align with, so the grid spells the Space out
                    where the list draws its badge. */}
                {(row.caption ?? (showFolderWorkspace ? row.workspaceName : undefined)) && (
                  <span className={styles.caption}>{row.caption ?? row.workspaceName}</span>
                )}
                <span className={styles.meta}>
                  {creator && <FileCreatedBy creator={creator} size={14} />}
                  {size != null && <span className={styles.size}>{humanSize(size)}</span>}
                </span>
              </span>
            </Link>
            <span className={styles.actions}>
              <IconButton
                size="small"
                aria-label={t('files.share.title')}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onShare(row);
                }}
              >
                <Share2 size={16} aria-hidden />
              </IconButton>
              <OverflowMenu groups={actionsFor(row)} ariaLabel={t('agentCreations.table.options')} onOpen={() => onRowMenu?.(row)} />
            </span>
          </Card>
        );
      })}
    </div>
  );
}

/** A folder as a tile: the same square as a file's thumbnail, with a folder glyph instead of a preview, its name
 *  and how many files are anywhere beneath it. */
function FolderTile({
  folder,
  workspaceNames,
  onOpen,
  showWorkspace = false,
}: {
  folder: FileFolderRow;
  workspaceNames: Record<string, string>;
  onOpen: (path: string) => void;
  /** Name the owning Space under the folder — the merged root only, where two Spaces can each have an "images". */
  showWorkspace?: boolean;
}) {
  const { t } = useTranslation();
  const segments = pathSegments(folder.path);
  const title = segments.length === 1 ? (workspaceNames[folder.name] ?? folder.name) : folder.name;
  const count = folderCountLabel(folder, t);
  const space = showWorkspace ? folderWorkspaceName(folder.path, workspaceNames) : '';
  return (
    <Card variant="outline" size="sm" interactive className={styles.tile} disablePadding>
      <div
        className={styles.tileLink}
        role="button"
        tabIndex={0}
        aria-label={title}
        onClick={() => onOpen(folder.path)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onOpen(folder.path);
          }
        }}
      >
        {/* Sized and tilted like the file tiles' page thumb, so a folder and a file read as one grid rather
            than a big illustration beside a small outline. */}
        <span className={styles.folderGraphic}>
          <Folder size={80} strokeWidth={1.1} aria-hidden />
        </span>
        <span className={styles.body}>
          <span className={styles.name}>{title}</span>
          {space && <span className={styles.caption}>{space}</span>}
          <span className={styles.meta}>
            <span className={styles.size}>{count}</span>
            <ChevronRight size={14} aria-hidden className="dir-flip" />
          </span>
        </span>
      </div>
    </Card>
  );
}
